import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, Building2, User, Check } from "lucide-react";
import HeroSection from "@/components/HeroSection";
import StatsSection from "@/components/StatsSection";

const ServicesSection = lazy(() => import("@/components/ServicesSection"));
const WhyUsSection = lazy(() => import("@/components/WhyUsSection"));
const ProcessSection = lazy(() => import("@/components/ProcessSection"));
const PricingSection = lazy(() => import("@/components/PricingSection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const AboutSection = lazy(() => import("@/components/AboutSection"));
const AreaSection = lazy(() => import("@/components/AreaSection"));
const ContactFormSection = lazy(() => import("@/components/ContactFormSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const BusinessContent = lazy(() => import("@/components/BusinessContent"));
const SeoContentSection = lazy(() => import("@/components/SeoContentSection"));
const FaqSection = lazy(() => import("@/components/FaqSection"));
const Footer = lazy(() => import("@/components/Footer"));
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const GallerySection = lazy(() => import("@/components/GallerySection"));
const CostCalculator = lazy(() => import("@/components/CostCalculator"));
const BookingSection = lazy(() => import("@/components/BookingSection"));
const PromoPopup = lazy(() => import("@/components/PromoPopup"));
const DarkModeToggle = lazy(() => import("@/components/DarkModeToggle"));

type ClientType = "individual" | "business";

const navLinks = [
  { href: "#uslugi", label: "Usługi" },
  { href: "#cennik", label: "Cennik" },
  { href: "#o-nas", label: "O nas" },
  { href: "#formularz", label: "Kontakt" },
];

const serviceSubLinks = [
  { href: "#hydraulika", label: "💧 Hydraulika" },
  { href: "#elektryka", label: "⚡ Elektryka" },
  { href: "#agd", label: "🍳 Serwis AGD" },
  { href: "#tv-rtv", label: "📺 Montaż TV / RTV" },
  { href: "#zlota-raczka", label: "🔧 Złota rączka" },
  { href: "#komputery", label: "💻 Komputery / Laptopy" },
  { href: "#stolarz", label: "🪵 Stolarz / Meble na wymiar" },
  { href: "#tynki", label: "🎨 Tynki ozdobne / Dekoracyjne" },
  { href: "#remonty", label: "🏗️ Kompleksowe remonty" },
  { href: "#slusarz", label: "🔑 Ślusarz" },
  { href: "#strony-www", label: "🌐 Strony internetowe" },
];

const serviceSubLinksB2B = [
  { href: "#zlota-raczka-b2b", label: "🔧 Złota Rączka B2B" },
  { href: "#serwis-techniczny-b2b", label: "🔧 Serwis Techniczny" },
  { href: "#logistyka-b2b", label: "🚚 Logistyka i Relokacje" },
  { href: "#meble-b2b", label: "🪵 Meble i Zabudowy" },
  { href: "#it-multimedia-b2b", label: "💻 IT i Multimedia" },
  { href: "#remonty-b2b", label: "🏗️ Remonty Obiektów" },
  { href: "#strony-www-b2b", label: "🌐 Strony internetowe" },
];

const Index = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
  const [clientType, setClientType] = useState<ClientType>("individual");
  const [activeNavLink, setActiveNavLink] = useState<string | null>(null);
  const desktopDropdownRef = useRef<HTMLDivElement>(null);

  const handleClientTypeChange = (type: ClientType) => {
    setClientType(type);
    setServicesOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (desktopDropdownRef.current && !desktopDropdownRef.current.contains(e.target as Node)) {
        setDesktopServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-foreground/90 backdrop-blur-md border-b border-background/10">
        <div className="container max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <button
            className="flex md:hidden p-2 rounded-md text-background hover:bg-background/10 transition-all shrink-0"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <a href="#" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="font-heading font-bold text-lg text-background shrink-0 hover:opacity-90 transition-opacity">
            Pan Fixer<span className="text-accent"> – Usterka STOP</span>
          </a>

          <nav className="hidden md:flex items-center gap-1 text-sm font-medium flex-1 justify-center">
            <button
              onClick={() => handleClientTypeChange("individual")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all text-sm font-semibold ${
                clientType === "individual"
                  ? "text-accent bg-background/10"
                  : "text-background/70 hover:text-accent hover:bg-background/10"
              }`}
            >
              <User className="w-4 h-4" />
              Prywatnie
            </button>
            <button
              onClick={() => handleClientTypeChange("business")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all text-sm font-semibold ${
                clientType === "business"
                  ? "text-accent bg-background/10"
                  : "text-background/70 hover:text-accent hover:bg-background/10"
              }`}
            >
              <Building2 className="w-4 h-4" />
              Dla firm
            </button>
            <span className="w-px h-5 bg-background/20 mx-1" />

            <div ref={desktopDropdownRef} className="relative">
              <button
                onClick={() => setDesktopServicesOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-background/70 hover:text-accent px-3 py-1.5 rounded-md hover:bg-background/10 transition-all"
              >
                Usługi
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${desktopServicesOpen ? "rotate-180" : ""}`} />
              </button>
              {desktopServicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-foreground/95 backdrop-blur-md border border-background/10 rounded-lg shadow-xl py-2 z-50">
                  {(clientType === "individual" ? serviceSubLinks : serviceSubLinksB2B).map((sub) => (
                    <a
                      key={sub.label}
                      href={sub.href}
                      onClick={() => setDesktopServicesOpen(false)}
                      className="block text-background/70 hover:text-accent hover:bg-background/10 py-2 px-4 text-sm transition-all"
                    >
                      {sub.label}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <a
                key={link.href}
                href={clientType === "business" ? (
                  link.href === "#formularz" ? "#kontakt-b2b" :
                  link.href === "#cennik" ? "#cennik-b2b" :
                  link.href === "#o-nas" ? "#o-nas-b2b" :
                  link.href
                ) : link.href}
                className="text-background/70 hover:text-accent px-3 py-1.5 rounded-md hover:bg-background/10 transition-all"
              >
                {link.label}
              </a>
            ))}

            <a
              href={clientType === "business" ? "#faq-b2b" : "#faq"}
              className="text-background/70 hover:text-accent px-3 py-1.5 rounded-md hover:bg-background/10 transition-all"
            >
              FAQ
            </a>

            <Link
              to="/blog"
              className="text-background/70 hover:text-accent px-3 py-1.5 rounded-md hover:bg-background/10 transition-all"
            >
              Blog
            </Link>
          </nav>

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Suspense fallback={null}><DarkModeToggle /></Suspense>
            <a
              href="tel:+48788381508"
              className="hidden sm:inline-flex items-center gap-2 bg-accent text-accent-foreground font-semibold px-5 py-2 rounded-lg text-sm hover:brightness-110 transition-all"
            >
              📞 788 381 508
            </a>
            <a
              href="tel:+48788381508"
              aria-label="Zadzwoń"
              className="sm:hidden inline-flex items-center justify-center bg-accent text-accent-foreground font-semibold w-10 h-10 rounded-lg text-base hover:brightness-110 transition-all"
            >
              📞
            </a>
          </div>
        </div>

        {menuOpen && (
          <nav className="md:hidden bg-foreground/95 border-t border-background/10 px-4 py-3 flex flex-col gap-0.5">
            <div className="flex gap-2 mb-2 pb-2 border-b border-background/10">
              <button
                onClick={() => handleClientTypeChange("individual")}
                className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold transition-all relative ${
                  clientType === "individual"
                    ? "bg-accent text-accent-foreground ring-2 ring-accent shadow-md"
                    : "border border-background/20 text-background/70 hover:text-accent"
                }`}
              >
                {clientType === "individual" && <Check className="w-4 h-4 animate-in fade-in" />}
                <User className="w-4 h-4" />
                Prywatnie
              </button>
              <button
                onClick={() => handleClientTypeChange("business")}
                className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold transition-all relative ${
                  clientType === "business"
                    ? "bg-accent text-accent-foreground ring-2 ring-accent shadow-md"
                    : "border border-background/20 text-background/70 hover:text-accent"
                }`}
              >
                {clientType === "business" && <Check className="w-4 h-4 animate-in fade-in" />}
                <Building2 className="w-4 h-4" />
                Dla firm
              </button>
            </div>

            <button
              onClick={() => { setServicesOpen((v) => !v); setActiveNavLink("uslugi"); }}
              className={`flex items-center justify-between w-full py-2 px-3 rounded-md transition-all text-base font-medium ${
                activeNavLink === "uslugi"
                  ? "text-accent border border-accent/50 bg-accent/10"
                  : "text-background/80 hover:text-accent hover:bg-background/10"
              }`}
            >
              <span>Usługi</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
            </button>

            {servicesOpen && (
              <div className="ml-3 mt-0.5 mb-1 flex flex-col gap-0.5 border-l-2 border-accent/40 pl-3">
                {(clientType === "individual" ? serviceSubLinks : serviceSubLinksB2B).map((sub) => (
                  <a
                    key={sub.label}
                    href={sub.href}
                    onClick={() => { setMenuOpen(false); setServicesOpen(false); }}
                    className="text-background/70 hover:text-accent py-1.5 px-2 rounded-md hover:bg-background/10 transition-all text-sm"
                  >
                    {sub.label}
                  </a>
                ))}
              </div>
            )}

            {navLinks.slice(1).map((link) => {
              const resolvedHref = clientType === "business" ? (
                link.href === "#formularz" ? "#kontakt-b2b" :
                link.href === "#cennik" ? "#cennik-b2b" :
                link.href === "#o-nas" ? "#o-nas-b2b" :
                link.href
              ) : link.href;
              const linkKey = link.href.replace("#", "");
              return (
                <a
                  key={link.href}
                  href={resolvedHref}
                  onClick={() => { setMenuOpen(false); setActiveNavLink(linkKey); }}
                  className={`py-2 px-3 rounded-md transition-all text-base font-medium ${
                    activeNavLink === linkKey
                      ? "text-accent border border-accent/50 bg-accent/10"
                      : "text-background/80 hover:text-accent hover:bg-background/10"
                  }`}
                >
                  {link.label}
                </a>
              );
            })}

            <a
              href={clientType === "business" ? "#faq-b2b" : "#faq"}
              onClick={() => { setMenuOpen(false); setActiveNavLink("faq"); }}
              className={`py-2 px-3 rounded-md transition-all text-base font-medium ${
                activeNavLink === "faq"
                  ? "text-accent border border-accent/50 bg-accent/10"
                  : "text-background/80 hover:text-accent hover:bg-background/10"
              }`}
            >
              ❓ FAQ
            </a>

            <Link
              to="/blog"
              onClick={() => setMenuOpen(false)}
              className="py-2 px-3 rounded-md transition-all text-base font-medium text-background/80 hover:text-accent hover:bg-background/10"
            >
              📝 Blog
            </Link>
          </nav>
        )}
      </header>

      <main>
        <HeroSection clientType={clientType} onClientTypeChange={handleClientTypeChange} />
        <StatsSection />

        <Suspense fallback={null}>
          {clientType === "individual" ? (
            <>
              <ServicesSection />
              <GallerySection />
              <WhyUsSection />
              <ProcessSection />
              <CostCalculator />
              <PricingSection />
              <BookingSection />
              <TestimonialsSection />
              <AboutSection />
              <FaqSection />
              <ContactFormSection />
              <AreaSection />
              <ContactSection />
            </>
          ) : (
            <BusinessContent />
          )}
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <SeoContentSection />
        <Footer />
        <WhatsAppButton />
        <CookieConsent />
        <PromoPopup />
      </Suspense>
    </>
  );
};

export default Index;
